import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Apple, Activity, Trophy, Star, TrendingUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import apiClient from '@/api';
import Navbar from '@/components/Navbar';

const AVATAR_EMOJIS = ['🌱', '🌿', '🪴', '🌸', '🌺', '🌻', '✨', '🌟'];

export default function Profile({ user }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await apiClient.get('/my/progress');
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectAppleHealth = () => {
    toast.info('Apple Health integration coming soon!');
  };

  const connectGoogleFit = () => {
    toast.info('Google Fit integration coming soon!');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await apiClient.delete('/auth/me');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.success('Your account has been deleted');
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error('Something went wrong deleting your account. Please try again.');
      setDeleting(false);
    }
  };

  const xpProgress = progress?.next_level_info 
    ? ((progress.xp - progress.current_level_info.xp_required) / (progress.next_level_info.xp_required - progress.current_level_info.xp_required)) * 100
    : 100;

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="profile-page">
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight mb-8 text-[#5C4A42]">
          YOUR <span className="text-[#D4A574]">PROFILE</span>
        </h1>

        <div className="space-y-6">
          {/* User Info with Avatar & Level */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="warm-card border border-[#D4C4B0] p-8"
            data-testid="user-info"
          >
            <div className="flex items-start gap-6 mb-6">
              {/* Avatar */}
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-[#D4A574] to-[#8B7355] rounded-full flex items-center justify-center text-5xl">
                  {loading ? '...' : AVATAR_EMOJIS[progress?.avatar_stage - 1] || '🌱'}
                </div>
                <p className="text-sm font-bold text-[#D4A574] mt-2 uppercase">
                  {loading ? 'Loading...' : progress?.current_level_info?.name || 'Seedling'}
                </p>
              </div>
              
              {/* User Details */}
              <div className="flex-1">
                <h2 className="text-3xl font-black uppercase text-[#5C4A42]">{user?.name}</h2>
                <p className="text-[#8B7355] mb-4">{user?.email}</p>
                
                {/* Level & XP */}
                {!loading && progress && (
                  <div className="bg-[#FAF9F7] p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-[#5C4A42]">Level {progress.level}</span>
                      <span className="text-sm text-[#8B7355]">{progress.xp} XP</span>
                    </div>
                    <Progress value={xpProgress} className="h-2 bg-[#E8E3DC]" />
                    {progress.next_level_info && (
                      <p className="text-xs text-[#A89284] mt-1">
                        {progress.xp_to_next_level} XP to Level {progress.level + 1}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4 pt-6 border-t border-[#D4C4B0]">
              <div className="text-center">
                <p className="text-2xl font-black text-[#D4A574]">{progress?.points || 0}</p>
                <p className="text-xs text-[#8B7355] uppercase">Points</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-black text-[#D4A574]">{progress?.level || 1}</p>
                <p className="text-xs text-[#8B7355] uppercase">Level</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-black text-[#D4A574]">{progress?.total_badges || 0}</p>
                <p className="text-xs text-[#8B7355] uppercase">Badges</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-black text-[#D4A574]">{user?.total_workouts || 0}</p>
                <p className="text-xs text-[#8B7355] uppercase">Workouts</p>
              </div>
            </div>
          </motion.div>

          {/* Earned Badges */}
          {progress?.badges && progress.badges.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="warm-card border border-[#D4C4B0] p-8"
            >
              <h2 className="text-2xl font-bold uppercase mb-6 text-[#5C4A42] flex items-center gap-2">
                <Trophy className="w-6 h-6 text-[#D4A574]" />
                EARNED BADGES
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {progress.badges.map((badge) => (
                  <div
                    key={badge.id}
                    className="bg-gradient-to-br from-[#D4A574] to-[#8B7355] p-4 rounded-lg text-center text-white"
                  >
                    <div className="text-3xl mb-2">{badge.icon}</div>
                    <p className="text-xs font-bold uppercase">{badge.name}</p>
                    <p className="text-xs opacity-80">+{badge.points_reward} pts</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Fitness Integrations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="warm-card border border-[#D4C4B0] p-8"
            data-testid="integrations"
          >
            <h2 className="text-2xl font-bold uppercase mb-6 text-[#5C4A42]">FITNESS INTEGRATIONS</h2>
            
            <div className="space-y-4">
              {/* Apple Health */}
              <div className="flex items-center justify-between p-4 bg-[#FAF9F7] border border-[#D4C4B0] rounded-lg">
                <div className="flex items-center gap-4">
                  <Apple className="w-8 h-8 text-[#5C4A42]" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-bold uppercase text-[#5C4A42]">APPLE HEALTH</h3>
                    <p className="text-sm text-[#8B7355]">Sync steps, calories, and activity</p>
                  </div>
                </div>
                <Button
                  onClick={connectAppleHealth}
                  data-testid="connect-apple-health-btn"
                  variant="outline"
                  className="border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574] hover:text-white uppercase"
                >
                  CONNECT
                </Button>
              </div>

              {/* Google Fit */}
              <div className="flex items-center justify-between p-4 bg-[#FAF9F7] border border-[#D4C4B0] rounded-lg">
                <div className="flex items-center gap-4">
                  <Activity className="w-8 h-8 text-[#8B7355]" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-bold uppercase text-[#5C4A42]">GOOGLE FIT</h3>
                    <p className="text-sm text-[#8B7355]">Track workouts and health metrics</p>
                  </div>
                </div>
                <Button
                  onClick={connectGoogleFit}
                  data-testid="connect-google-fit-btn"
                  variant="outline"
                  className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white uppercase"
                >
                  CONNECT
                </Button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-[#FFF4E6] border border-[#D4A574] rounded-lg">
              <p className="text-xs text-[#8B7355]">
                <span className="text-[#D4A574] font-bold">INFRASTRUCTURE READY:</span> The app is prepared for Apple Health and Google Fit integration. Add your API credentials to enable automatic syncing.
              </p>
            </div>
          </motion.div>

          {/* Account Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="warm-card border border-[#D4C4B0] p-8"
          >
            <h2 className="text-2xl font-bold uppercase mb-6 text-[#5C4A42]">ACCOUNT INFO</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-[#8B7355] uppercase font-bold tracking-widest mb-1">Member Since</p>
                <p className="text-[#5C4A42] font-bold">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#8B7355] uppercase font-bold tracking-widest mb-1">Referral Code</p>
                <p className="text-[#D4A574] font-mono font-bold">{user?.referral_code || 'N/A'}</p>
              </div>
            </div>
          </motion.div>

          {/* Danger Zone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="warm-card border border-red-300 p-8"
            data-testid="danger-zone"
          >
            <h2 className="text-2xl font-bold uppercase mb-2 text-red-600">Danger Zone</h2>
            <p className="text-[#8B7355] mb-6">
              Permanently delete your account and all associated data: workouts, goals, weight
              history, meal plans, and group memberships. This cannot be undone.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="delete-account-btn"
                  variant="outline"
                  className="border-red-400 text-red-600 hover:bg-red-600 hover:text-white uppercase"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently deletes your account and all of your data. This action
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="confirm-delete-account-btn"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deleting ? 'Deleting...' : 'Yes, delete my account'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </motion.div>
        </div>
      </div>
    </div>
  );
}